// Estilos próprios da tela de Empresas (/companies) e da filha Planos.
// O que é comum a todas as telas Aeros (header, tableCard, pills, botões)
// vem de Campaigns/campaignPageStyles.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

// Colunas do mockup: ID · Empresa · Plano/Valor · Status · Criada · Vencimento
// · Último acesso · Uso/SMS · Ações
export const TABLE_GRID =
  "52px minmax(0,1.9fr) minmax(0,1.2fr) minmax(0,1fr) minmax(0,1.3fr) 110px 130px 130px 130px 152px";

const useCompanyPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";

  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },

    // Filtro de status: no mockup é um botão com borda, não um campo.
    statusFilter: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: "transparent",
        fontSize: 12.5,
        fontWeight: 600,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isLight
          ? theme.palette.aeros.borderStrong
          : "rgba(255,255,255,0.14)",
      },
      "& .MuiSelect-root": {
        padding: "9px 30px 9px 12px",
        color: theme.palette.text.secondary,
      },
      "& .MuiSelect-icon": { color: theme.palette.text.secondary },
    },

    // ---- KPIs ----
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(5, minmax(0,1fr))",
      gap: 12,
      marginBottom: 16,
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "repeat(3, minmax(0,1fr))",
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      },
    },
    kpiCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "14px 16px",
      minWidth: 0,
    },
    kpiLabel: {
      fontSize: 11,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    kpiValue: {
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      marginTop: 4,
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    kpiHint: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    kpiSuccess: { color: stateText(theme, AEROS.success, "success") },
    kpiError: { color: stateText(theme, AEROS.error, "error") },
    kpiWarning: { color: stateText(theme, AEROS.warning, "warning") },
    kpiInfo: { color: stateText(theme, AEROS.cyan, "info") },

    // ---- linhas ----
    idCell: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    // Vencida em vermelho, inativa esmaecida — a legenda no rodapé explica.
    // Cabeçalho e linhas no mesmo wrapper rolável (o tableCard é overflow hidden).
    tableScroll: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
    tableInner: { minWidth: 1224 },
    subscriptionText: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    rowExpired: { backgroundColor: alpha(AEROS.error, 0.05) },
    rowInactive: { opacity: 0.62 },
    companyCell: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
    },
    companyAvatar: {
      width: 34,
      height: 34,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      fontSize: 12,
      fontWeight: 700,
    },
    companyName: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    companyEmail: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    planName: {
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    planValue: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    dateCell: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    dueDate: {
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    dueDateLate: {
      color: stateText(theme, AEROS.error, "error"),
      fontWeight: 700,
    },
    dueHint: { fontSize: 10.5, color: theme.palette.aeros.textTertiary },
    usageText: {
      fontSize: 11.5,
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums",
    },
    smsTag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      fontWeight: 600,
      marginTop: 2,
      color: stateText(theme, AEROS.cyan, "info"),
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },

    // ---- rodapé da tabela ----
    tableFooter: {
      display: "flex",
      alignItems: "center",
      padding: "12px 18px",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      gap: 8,
      flexWrap: "wrap",
    },
    pagination: { marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" },
    pageButton: {
      minWidth: 30,
      padding: "3px 9px",
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    pageButtonActive: {
      backgroundColor: alpha(AEROS.defaultPrimary, 0.18),
      color: theme.palette.text.primary,
    },
    pageArrow: {
      padding: 4,
      borderRadius: 6,
      border: `1px solid ${border}`,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    packagesBlock: { marginTop: 24 },
    footNote: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginTop: 12,
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
  };
});

export default useCompanyPageStyles;
